import { useMemo } from 'react';
import { useI18n } from '../../../i18n/useI18n';
import {
  buildCrawlJobTimeSeriesChartData,
  buildStackedCrawlJobTimeSeriesChartData,
  resolveCrawlJobTimeSeriesTenantLabel,
} from '../crawlJobTimeSeriesChart';
import '../../costs/components/CostChart.css';
import './LacCrawlJobTimeSeriesChart.css';
import { LacCrawlJobTimeSeriesChartHeader } from './LacCrawlJobTimeSeriesChartHeader';
import { LacCrawlJobTimeSeriesChartPlot } from './LacCrawlJobTimeSeriesChartPlot';
import {
  resolveCrawlJobChartYMax,
  type LacCrawlJobTimeSeriesChartProps,
} from './lacCrawlJobTimeSeriesChartModel';

export type { LacCrawlJobTimeSeriesChartProps } from './lacCrawlJobTimeSeriesChartModel';

export function LacCrawlJobTimeSeriesChart({
  mode,
  onModeChange,
  fromDay,
  toDay,
  fromMonth,
  toMonth,
  rangeLabel,
  canGoNext,
  onPrev,
  onNext,
  query,
  stackedByTenant = false,
  tenantLabels,
  aside,
}: LacCrawlJobTimeSeriesChartProps) {
  const { locale, translate } = useI18n();

  const singleChartData = useMemo(() => {
    if (stackedByTenant || query.isLoading || query.isError) {
      return [];
    }
    return buildCrawlJobTimeSeriesChartData(
      query.data ?? [],
      mode,
      fromDay,
      toDay,
      fromMonth,
      toMonth,
    );
  }, [
    stackedByTenant,
    query.isLoading,
    query.isError,
    query.data,
    mode,
    fromDay,
    toDay,
    fromMonth,
    toMonth,
  ]);

  const stackedChartData = useMemo(() => {
    if (!stackedByTenant || query.isLoading || query.isError) {
      return { points: [], tenants: [] };
    }
    return buildStackedCrawlJobTimeSeriesChartData(
      query.data ?? [],
      mode,
      fromDay,
      toDay,
      fromMonth,
      toMonth,
    );
  }, [
    stackedByTenant,
    query.isLoading,
    query.isError,
    query.data,
    mode,
    fromDay,
    toDay,
    fromMonth,
    toMonth,
  ]);

  const chartData = stackedByTenant ? stackedChartData.points : singleChartData;
  const tenants = stackedChartData.tenants;

  const resolvedTenantLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    for (const tenant of tenants) {
      labels[tenant] = resolveCrawlJobTimeSeriesTenantLabel(
        tenant,
        tenantLabels,
      );
    }
    return labels;
  }, [tenantLabels, tenants]);

  const yMax = useMemo(
    () =>
      resolveCrawlJobChartYMax(
        chartData as Array<Record<string, string | number>>,
        stackedByTenant,
      ),
    [chartData, stackedByTenant],
  );

  return (
    <section
      className="lac-crawl-job-time-series-chart cost-chart"
      aria-label={translate('lac.websiteCrawls.chart.legend')}
    >
      <LacCrawlJobTimeSeriesChartHeader
        title={translate('lac.websiteCrawls.chart.title')}
        navLabel={translate('organisationManagement.costs.chart.navLabel')}
        previousLabel={translate('organisationManagement.costs.chart.previous')}
        nextLabel={translate('organisationManagement.costs.chart.next')}
        viewToggleLabel={translate(
          'organisationManagement.costs.chart.viewToggleLabel',
        )}
        dailyLabel={translate('organisationManagement.costs.chart.daily')}
        monthlyLabel={translate('organisationManagement.costs.chart.monthly')}
        rangeLabel={rangeLabel}
        canGoNext={canGoNext}
        mode={mode}
        onModeChange={onModeChange}
        onPrev={onPrev}
        onNext={onNext}
      />

      <div
        className={[
          'cost-chart__body',
          aside != null && 'lac-crawl-job-time-series-chart__body--with-aside',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="lac-crawl-job-time-series-chart__chart">
          <LacCrawlJobTimeSeriesChartPlot
            isLoading={query.isLoading}
            isError={query.isError}
            chartData={chartData as Array<Record<string, string | number>>}
            stackedByTenant={stackedByTenant}
            tenants={tenants}
            resolvedTenantLabels={resolvedTenantLabels}
            yMax={yMax}
            mode={mode}
            locale={locale}
            countLabel={translate('lac.websiteCrawls.chart.count')}
            loadingLabel={translate('lac.websiteCrawls.chart.loading')}
            loadErrorLabel={translate('lac.websiteCrawls.chart.loadError')}
            emptyLabel={translate('lac.websiteCrawls.chart.empty')}
          />
        </div>
        {aside ? (
          <div className="lac-crawl-job-time-series-chart__aside">{aside}</div>
        ) : null}
      </div>
    </section>
  );
}
